/* ============================================================================
   Yuvakart - predictive search
   ============================================================================

   LAYOUT

     +------------------------------+------------------------------+
     |  SUGGESTIONS                 |  PAGES                       |
     +--------------+---------------+------------------------------+
     |  PRODUCTS    |  COLLECTIONS  |  ARTICLES                    |
     +--------------+---------------+------------------------------+
     |                   View all                                  |
     +-------------------------------------------------------------+

   SPEED

   The first build fetched /products.json, which returns the complete product
   object - body_html, every variant, every image. For this catalogue that is
   roughly 400 KB, and the panel awaited it before painting. That single choice
   caused both reported faults: suggestions felt slow, and misspellings looked
   broken because the fuzzy index usually had not arrived yet.

   Three changes fix it:

     1. The index now comes from templates/collection.ykindex.liquid - seven
        fields per product, about 18 KB for the whole catalogue, CDN cached.
     2. It is fetched the moment the script runs and kept in localStorage for
        24 hours, so it is warm on every later page and every later visit.
     3. Nothing ever waits on it. Local matches paint instantly, then Shopify's
        API results merge in when they land. Responses are memoised, so
        backspacing re-renders with no network at all.

   SIMILAR SPELLING

   Four matchers run per word, best score wins:

     - prefix and substring, which handle ordinary typing
     - an alias table for Indian transliteration variants, where the same bead
       is written rudraksh / rudraksha / rudrax and the same goddess is laxmi
       or lakshmi
     - bounded Levenshtein distance, which catches one or two wrong, missing or
       swapped letters (bracelte -> bracelet)
     - a bigram Dice coefficient, which catches heavier mangling (rudrash)

   Multi-word queries score per word and average, so "rudrash braclet" still
   lands on the Rudraksha bracelets. Correctly spelled matches always outrank
   fuzzy ones, so good spelling is never punished.

   No app, no dependency, no third-party request.
   ============================================================================ */
(function () {
  'use strict';

  var CFG = {
    debounce: 90,
    minChars: 2,
    fuzzyMin: 0.34,
    cacheKey: 'ykps_idx_v3',
    cacheHours: 24,
    limits: { product: 4, collection: 3, article: 3, page: 4, term: 6 }
  };

  var STOP = ['for','the','with','and','your','from','set','of','a','an','in','on','to','by','pcs','pc','piece','inch','mm','cm'];

  /* Transliteration variants people actually type. Left is what they type,
     right is what the catalogue calls it. */
  var ALIAS = {
    rudraksh:'rudraksha', rudrakash:'rudraksha', rudraksa:'rudraksha', rudrax:'rudraksha',
    rudraaksha:'rudraksha', ruderaksh:'rudraksha', rudrakha:'rudraksha', rudrraksh:'rudraksha',
    mukhi:'mukhi', mukh:'mukhi', mukhee:'mukhi',
    maala:'mala', malla:'mala', malas:'mala', maal:'mala',
    jap:'japa', jaap:'japa', japam:'japa',
    braclet:'bracelet', bracelate:'bracelet', bracelett:'bracelet', braslet:'bracelet',
    bracelat:'bracelet', brecelet:'bracelet', bracelt:'bracelet',
    pendent:'pendant', pendat:'pendant', pendnt:'pendant',
    sphatic:'sphatik', spatik:'sphatik', sfatik:'sphatik',
    cristal:'crystal', crystle:'crystal', kristal:'crystal',
    pirite:'pyrite', pyrit:'pyrite', pairite:'pyrite',
    yentra:'yantra', jantra:'yantra', yentr:'yantra', yanta:'yantra',
    kawach:'kavach', kavatch:'kavach', kavch:'kavach',
    gomati:'gomti', gomatti:'gomti',
    tulasi:'tulsi', tulshi:'tulsi',
    laxmi:'lakshmi', mahalaxmi:'lakshmi', laxmiji:'lakshmi',
    ganpati:'ganesh', ganpathi:'ganesh', ganesha:'ganesh', ganeshji:'ganesh', ganapati:'ganesh',
    hanumanji:'hanuman', hanumaan:'hanuman', bajrangbali:'hanuman',
    shivling:'shivling', shivlinga:'shivling', shivlingam:'shivling',
    mahadev:'shiva', shiv:'shiva', shivji:'shiva',
    saraswati:'saraswati', sarswati:'saraswati',
    astadhatu:'ashtadhatu', asthadhatu:'ashtadhatu',
    deepak:'diya', dia:'diya', deeya:'diya',
    kachua:'tortoise', kachhua:'tortoise', kachuwa:'tortoise',
    brass:'brass', bras:'brass',
    amethist:'amethyst', ametist:'amethyst',
    citrin:'citrine', sitrine:'citrine',
    rosequartz:'rose quartz', quarts:'quartz', quartez:'quartz',
    selenit:'selenite', calcedony:'chalcedony',
    combo:'combo', kombo:'combo'
  };

  var LINE = '#EDE6D8';
  var HEAD = '#8A8474';
  var INK  = '#1F1B16';
  var MUTE = '#9B9384';

  /* ---------------------------------------------------------------- css -- */

  var CSS = [
    '.yk-suggest.ykps-on{padding:0 !important;border-radius:4px !important;overflow:hidden !important;',
    'border:1px solid ' + LINE + ' !important;box-shadow:0 20px 52px rgba(58,38,12,.15) !important;background:#fff !important}',

    '.ykps{font-family:inherit;color:' + INK + ';text-align:left}',

    '.ykps__row{display:grid}',
    '.ykps__row+.ykps__row{border-top:1px solid ' + LINE + '}',
    '.ykps__col{padding:24px 30px 26px;min-width:0;position:relative}',
    '.ykps__col+.ykps__col{border-left:1px solid ' + LINE + '}',
    '.ykps__grp+.ykps__grp{margin-top:26px}',

    '.ykps h5{margin:0 0 18px;font-family:var(--yk-serif,Georgia,serif);font-size:13px;font-weight:500;',
    'letter-spacing:.14em;text-transform:uppercase;color:' + HEAD + '}',

    '.ykps__terms{display:flex;flex-wrap:wrap;gap:11px 30px}',
    '.ykps__term{font-size:15px;line-height:1.3;color:' + INK + ';text-decoration:none;',
    'border-bottom:1px solid transparent;padding-bottom:1px;transition:color .15s ease,border-color .15s ease}',
    '.ykps__term:hover,.ykps__term.is-on{color:#8a6a1e;border-bottom-color:#B8912E}',

    '.ykps__li{display:flex;align-items:flex-start;gap:18px;padding:10px 8px;margin:0 -8px 2px;',
    'border-radius:6px;text-decoration:none;color:inherit;transition:background .15s ease}',
    '.ykps__li:hover,.ykps__li.is-on{background:#FBF7EE}',
    '.ykps__th{width:88px;height:88px;flex:none;border-radius:4px;overflow:hidden;',
    'background:#F6F1E6 center/cover no-repeat;display:flex;align-items:center;justify-content:center;',
    'font-size:22px;font-weight:700;color:#CFC5AC}',
    '.ykps__th--sm{width:84px;height:84px}',
    '.ykps__tx{min-width:0;flex:1;padding-top:2px}',
    '.ykps__kick{display:block;font-size:11.5px;letter-spacing:.13em;text-transform:uppercase;',
    'color:' + MUTE + ';margin-bottom:6px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}',
    '.ykps__t{display:-webkit-box;font-size:15px;line-height:1.38;font-weight:400;color:' + INK + ';',
    '-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
    '.ykps__pr{display:block;margin-top:8px;font-size:15px;font-weight:600;color:' + INK + '}',
    '.ykps__pr s{font-weight:400;font-size:13.5px;color:' + MUTE + ';margin-left:7px}',

    '.ykps__all{display:block;padding:18px;text-align:center;font-size:15px;font-weight:500;',
    'color:' + INK + ';background:#fff;border-top:1px solid ' + LINE + ';text-decoration:none;',
    'transition:background .15s ease,color .15s ease}',
    '.ykps__all:hover,.ykps__all.is-on{background:#FBF7EE;color:#8a6a1e}',

    '.ykps__none{padding:28px 30px;font-size:15px;color:#5A5449;line-height:1.6}',
    '.ykps__none b{color:' + INK + ';font-weight:600}',
    '.ykps__sk{padding:24px 30px}',
    '.ykps__skrow{display:flex;gap:18px;align-items:center;padding:9px 0}',
    '.ykps__skb{border-radius:4px;background:linear-gradient(90deg,#F4EFE4 25%,#FBF7EE 37%,#F4EFE4 63%);',
    'background-size:400% 100%;animation:ykpsSk 1.2s ease infinite}',
    '@keyframes ykpsSk{0%{background-position:100% 50%}100%{background-position:0 50%}}',

    '@media (min-width:861px){',
    '.yk-search .yk-suggest{width:min(940px,calc(100vw - 48px));right:auto}',
    '.ykps__row--main.n1{grid-template-columns:1fr}',
    '.ykps__row--main.n2{grid-template-columns:1fr 1fr}',
    '.ykps__row--main.n3{grid-template-columns:1fr 1fr 1fr}',
    '.ykps{max-height:min(72vh,660px);overflow-y:auto}',
    '}',
    '@media (max-width:860px){',
    '.ykps__row{grid-template-columns:1fr !important}',
    '.ykps__col{padding:18px 18px 20px}',
    '.ykps__col+.ykps__col{border-left:0;border-top:1px solid ' + LINE + '}',
    '.ykps__terms{gap:10px 22px}',
    '.ykps__th{width:72px;height:72px}',
    '.ykps__th--sm{width:68px;height:68px}',
    '.ykps__li{gap:14px}',
    '.ykps{max-height:70vh;overflow-y:auto;-webkit-overflow-scrolling:touch}',
    '}'
  ].join('');

  function injectCSS() {
    var old = document.getElementById('ykps-css');
    if (old && old.parentNode) old.parentNode.removeChild(old);
    var s = document.createElement('style');
    s.id = 'ykps-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* ------------------------------------------------------------ helpers -- */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (m) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m];
    });
  }

  function norm(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function alias(w) { return ALIAS[w] || w; }

  function bigrams(s) {
    var out = [];
    for (var i = 0; i < s.length - 1; i++) out.push(s.slice(i, i + 2));
    return out;
  }

  function dice(a, b) {
    var A = bigrams(a), B = bigrams(b);
    if (!A.length || !B.length) return 0;
    var bag = {}, hit = 0, i;
    for (i = 0; i < B.length; i++) bag[B[i]] = (bag[B[i]] || 0) + 1;
    for (i = 0; i < A.length; i++) if (bag[A[i]] > 0) { bag[A[i]]--; hit++; }
    return (2 * hit) / (A.length + B.length);
  }

  /* Levenshtein, abandoned as soon as it exceeds max - keeps it cheap. */
  function lev(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= lb; j++) prev[j] = j;
    for (i = 1; i <= la; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= lb; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1,
          prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;
      prev = cur.slice();
    }
    return prev[lb];
  }

  /* How close is one query word to one catalogue word? 0 to 1. */
  function wordScore(qw, tw) {
    if (qw === tw) return 1;
    if (tw.indexOf(qw) === 0) return 0.97;
    if (qw.length > 3 && qw.indexOf(tw) === 0) return 0.9;
    if (tw.indexOf(qw) > -1) return 0.86;
    var maxErr = qw.length >= 7 ? 2 : (qw.length >= 4 ? 1 : 0);
    if (maxErr && lev(qw, tw, maxErr) <= maxErr) return 0.84;
    return dice(qw, tw);
  }

  /* Whole-query score against one piece of text. Exact and prefix hits are
     scored far above anything fuzzy, so correct spelling always wins. */
  function score(q, text) {
    var t = norm(text);
    if (!t || !q) return 0;
    if (t === q) return 200;
    if (t.indexOf(q) === 0) return 180;

    var words = t.split(' '), i, k;
    for (i = 0; i < words.length; i++) if (words[i].indexOf(q) === 0) return 165;
    if (t.indexOf(q) > -1) return 150;

    var qws = q.split(' '), counted = 0, total = 0;
    for (k = 0; k < qws.length; k++) {
      var qw = qws[k];
      if (qw.length < 3) continue;
      var qa = alias(qw);
      counted++;
      var best = 0;
      for (i = 0; i < words.length; i++) {
        var tw = words[i];
        if (tw.length < 2) continue;
        var s = wordScore(qw, tw);
        if (qa !== qw) { var s2 = wordScore(qa, tw); if (s2 > s) s = s2; }
        if (s > best) best = s;
        if (best === 1) break;
      }
      if (best >= CFG.fuzzyMin) total += best;
    }
    if (!counted || !total) return 0;
    return Math.round((total / counted) * 120);
  }

  function inr(paise) {
    var n = Number(paise) / 100;
    if (!isFinite(n) || n <= 0) return '';
    var s = (n % 1 ? n.toFixed(2) : String(Math.round(n)));
    return '₹' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function inrPlain(v) {
    var n = parseFloat(v);
    if (!isFinite(n) || n <= 0) return '';
    var s = (n % 1 ? n.toFixed(2) : String(Math.round(n)));
    return '₹' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function thumb(url, px) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, '_' + px + 'x$1$2');
  }

  function root() {
    var r = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return r.replace(/\/$/, '');
  }

  function searchUrl(q) {
    return root() + '/search?q=' + encodeURIComponent(q) + '&type=product';
  }

  function imgOf(o) {
    return o.image || o.featured_image
      || (o.featured_media && o.featured_media.preview_image && o.featured_media.preview_image.src)
      || '';
  }

  function initial(title) {
    var t = norm(title);
    return t ? esc(t.charAt(0).toUpperCase()) : '';
  }

  /* ------------------------------------------------------------- index -- */

  var IDX = [], VOCAB = [], ready = false, onReady = [];

  function buildVocab() {
    var seen = {}, list = [];
    IDX.forEach(function (p) {
      var words = norm(p.t).split(' ').filter(function (w) {
        return w.length > 1 && STOP.indexOf(w) < 0;
      });
      for (var n = 1; n <= 3; n++) {
        for (var i = 0; i + n <= words.length; i++) {
          var ph = words.slice(i, i + n).join(' ');
          if (ph.length < 3 || seen[ph]) continue;
          seen[ph] = 1;
          list.push(ph);
        }
      }
    });
    VOCAB = list;
  }

  function finish() {
    buildVocab();
    ready = true;
    var fns = onReady.slice();
    onReady = [];
    fns.forEach(function (f) { try { f(); } catch (e) {} });
  }

  function fetchPage(page) {
    return fetch(root() + '/collections/all?view=ykindex' + (page > 1 ? '&page=' + page : ''),
      { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (txt) { return JSON.parse(txt); });
  }

  function loadIndex() {
    /* try the cache first - this is the common case */
    try {
      var raw = localStorage.getItem(CFG.cacheKey);
      if (raw) {
        var c = JSON.parse(raw);
        if (c && c.i && c.i.length && (Date.now() - c.t) < CFG.cacheHours * 3600e3) {
          IDX = c.i;
          finish();
          return;
        }
      }
    } catch (e) {}

    /* drop the old oversized cache from the previous build */
    try { sessionStorage.removeItem('ykps_idx'); } catch (e) {}

    fetchPage(1).then(function (d) {
      IDX = d.items || [];
      if (d.more) {
        finish();                       /* usable already - do not wait */
        return fetchPage(2).then(function (d2) {
          IDX = IDX.concat(d2.items || []);
          buildVocab();
          save();
        });
      }
      finish();
      save();
    }).catch(function () { finish(); });

    function save() {
      try {
        localStorage.setItem(CFG.cacheKey, JSON.stringify({ t: Date.now(), i: IDX }));
      } catch (e) {}
    }
  }

  function localProducts(q, limit) {
    if (!IDX.length) return [];
    var out = [];
    for (var i = 0; i < IDX.length; i++) {
      var p = IDX[i];
      var s = Math.max(score(q, p.t), score(q, p.y) * 0.8, score(q, p.g) * 0.7);
      if (s > 0) out.push({ s: s, p: p });
    }
    out.sort(function (a, b) { return b.s - a.s; });
    return out.slice(0, limit).map(function (o) {
      return {
        title: o.p.t, url: root() + '/products/' + o.p.h, image: o.p.i,
        paise: o.p.p, comparePaise: o.p.c, product_type: o.p.y, handle: o.p.h, local: 1
      };
    });
  }

  /* Does anything in the catalogue actually contain the query as typed? */
  function hasLiteral(q) {
    for (var i = 0; i < IDX.length; i++) {
      if (norm(IDX[i].t).indexOf(q) > -1) return true;
    }
    return false;
  }

  function localTerms(q, limit) {
    if (!VOCAB.length) return [];
    var out = [], i;
    for (i = 0; i < VOCAB.length; i++) {
      var s = score(q, VOCAB[i]);
      if (s > 0) out.push({ s: s, t: VOCAB[i] });
    }
    out.sort(function (a, b) {
      if (b.s !== a.s) return b.s - a.s;
      return a.t.length - b.t.length;
    });
    var seen = {}, res = [];
    for (i = 0; i < out.length && res.length < limit; i++) {
      if (out[i].t === q || seen[out[i].t]) continue;
      seen[out[i].t] = 1;
      res.push(out[i].t);
    }
    return res;
  }

  /* ------------------------------------------------------- shopify fetch -- */

  var memo = {};

  function suggest(q) {
    if (memo[q]) return Promise.resolve(memo[q]);
    var url = root() + '/search/suggest.json?q=' + encodeURIComponent(q)
      + '&resources[type]=product,collection,page,article,query'
      + '&resources[limit]=8'
      + '&resources[options][unavailable_products]=last'
      + '&resources[options][fields]=title,product_type,variants.title,vendor,tag';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var res = (d.resources && d.resources.results) || {};
        memo[q] = res;
        return res;
      })
      .catch(function () { return {}; });
  }

  /* -------------------------------------------------------------- render -- */

  function productLi(p) {
    var img = thumb(imgOf(p) || p.image, 200);
    var price = p.local ? inr(p.paise) : inrPlain(p.price);
    var was = '';
    if (p.local) {
      if (Number(p.comparePaise) > Number(p.paise)) was = '<s>' + inr(p.comparePaise) + '</s>';
    } else {
      var pn = parseFloat(p.price), cn = parseFloat(p.compare_at_price);
      if (!isNaN(cn) && !isNaN(pn) && cn > pn) was = '<s>' + inrPlain(cn) + '</s>';
    }
    return '<a class="ykps__li" data-ykps-item href="' + esc(p.url) + '">'
      + '<span class="ykps__th"' + (img ? ' style="background-image:url(' + esc(img) + ')"' : '') + '>'
      + (img ? '' : initial(p.title)) + '</span>'
      + '<span class="ykps__tx">'
      + (p.product_type ? '<span class="ykps__kick">' + esc(p.product_type) + '</span>' : '')
      + '<span class="ykps__t">' + esc(p.title) + '</span>'
      + (price ? '<span class="ykps__pr">' + price + was + '</span>' : '')
      + '</span></a>';
  }

  function mediaLi(it) {
    var img = thumb(imgOf(it), 200);
    return '<a class="ykps__li" data-ykps-item href="' + esc(it.url) + '">'
      + '<span class="ykps__th ykps__th--sm"' + (img ? ' style="background-image:url(' + esc(img) + ')"' : '') + '>'
      + (img ? '' : initial(it.title)) + '</span>'
      + '<span class="ykps__tx"><span class="ykps__t">' + esc(it.title) + '</span></span></a>';
  }

  function plainLi(it) {
    return '<a class="ykps__li" data-ykps-item href="' + esc(it.url) + '" style="padding:6px 8px">'
      + '<span class="ykps__tx" style="padding-top:0"><span class="ykps__t">' + esc(it.title) + '</span></span></a>';
  }

  function grp(title, inner) {
    if (!inner) return '';
    return '<div class="ykps__grp"><h5>' + title + '</h5>' + inner + '</div>';
  }

  function dedupe(list) {
    var seen = {}, out = [];
    list.forEach(function (p) {
      var k = String(p.handle || p.url || p.title || '').split('?')[0].replace(/^.*\/products\//, '');
      if (seen[k]) return;
      seen[k] = 1;
      out.push(p);
    });
    return out;
  }

  function build(q, res) {
    var products = (res.products || []).slice(0);
    var collections = (res.collections || []).slice(0, CFG.limits.collection);
    var pages = (res.pages || []).slice(0, CFG.limits.page);
    var articles = (res.articles || []).slice(0, CFG.limits.article);
    var terms = (res.queries || []).map(function (x) { return x.text; }).slice(0, CFG.limits.term);

    if (products.length < CFG.limits.product) {
      products = dedupe(products.concat(localProducts(q, CFG.limits.product * 3)));
    }
    products = products.slice(0, CFG.limits.product);

    /* Only call it a correction when nothing in the catalogue literally
       contains what was typed. */
    var corrected = products.length > 0 && !hasLiteral(q);

    if (terms.length < 4) {
      var have = {};
      terms.forEach(function (t) { have[norm(t)] = 1; });
      localTerms(q, CFG.limits.term - terms.length).forEach(function (t) {
        if (!have[t]) terms.push(t);
      });
    }

    if (!products.length && !collections.length && !pages.length && !articles.length && !terms.length) {
      return '<div class="ykps">'
        + '<div class="ykps__none">Nothing matched <b>' + esc(q) + '</b>. Try a shorter word — or browse the full range.</div>'
        + '<a class="ykps__all" data-ykps-item href="' + esc(root() + '/collections/all') + '">Browse all products</a>'
        + '</div>';
    }

    var html = '<div class="ykps">';

    var sug = terms.length
      ? '<div class="ykps__col"><h5>Suggestions</h5><div class="ykps__terms">'
        + terms.map(function (t) {
            return '<a class="ykps__term" data-ykps-item href="' + esc(searchUrl(t)) + '">' + esc(t) + '</a>';
          }).join('')
        + '</div></div>'
      : '';
    var pg = pages.length
      ? '<div class="ykps__col"><h5>Pages</h5>' + pages.map(plainLi).join('') + '</div>'
      : '';
    if (sug || pg) {
      html += '<div class="ykps__row ykps__row--top" style="grid-template-columns:'
        + (sug && pg ? '1fr 1fr' : '1fr') + '">' + sug + pg + '</div>';
    }

    var cA = products.length
      ? '<div class="ykps__col">' + grp(corrected ? 'Did you mean' : 'Products', products.map(productLi).join('')) + '</div>'
      : '';
    var cB = collections.length
      ? '<div class="ykps__col">' + grp('Collections', collections.map(mediaLi).join('')) + '</div>'
      : '';
    var cC = articles.length
      ? '<div class="ykps__col">' + grp('Articles', articles.map(mediaLi).join('')) + '</div>'
      : '';
    var n = (cA ? 1 : 0) + (cB ? 1 : 0) + (cC ? 1 : 0);
    if (n) html += '<div class="ykps__row ykps__row--main n' + n + '">' + cA + cB + cC + '</div>';

    html += '<a class="ykps__all" data-ykps-item href="' + esc(searchUrl(q)) + '">View all</a></div>';
    return html;
  }

  function skeleton() {
    var row = '<div class="ykps__skrow">'
      + '<div class="ykps__skb" style="width:88px;height:88px"></div>'
      + '<div style="flex:1">'
      + '<div class="ykps__skb" style="height:11px;width:34%"></div>'
      + '<div class="ykps__skb" style="height:13px;width:66%;margin-top:11px"></div>'
      + '<div class="ykps__skb" style="height:13px;width:28%;margin-top:11px"></div>'
      + '</div></div>';
    return '<div class="ykps"><div class="ykps__sk">' + row + row + '</div></div>';
  }

  /* ---------------------------------------------------------------- wire -- */

  loadIndex();   /* starts immediately, blocks nothing */

  function init() {
    var wrap = document.querySelector('.yk-search[data-yk-search]') || document.querySelector('.yk-search');
    if (!wrap) return;
    var input = wrap.querySelector('input[type="search"], input[name="q"]');
    if (!input || input.getAttribute('data-ykps')) return;
    input.setAttribute('data-ykps', '1');

    injectCSS();

    var panel = wrap.querySelector('.yk-suggest');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'yk-suggest';
      wrap.appendChild(panel);
    }
    var trending = panel.innerHTML;

    var timer = null, seq = 0, cursor = -1;

    function open() { wrap.classList.add('open'); place(); }
    function close() { wrap.classList.remove('open'); cursor = -1; }

    function results(html) {
      panel.classList.add('ykps-on');
      panel.innerHTML = html;
      cursor = -1;
    }

    function showTrending() {
      panel.classList.remove('ykps-on');
      panel.innerHTML = trending;
      cursor = -1;
    }

    function place() {
      if (window.innerWidth <= 860) { panel.style.left = ''; return; }
      panel.style.left = '0px';
      var r = panel.getBoundingClientRect();
      var over = r.right - (window.innerWidth - 16);
      if (over > 0) panel.style.left = (-Math.min(over, Math.max(r.left - 16, 0))) + 'px';
    }

    function items() { return panel.querySelectorAll('[data-ykps-item]'); }

    function move(dir) {
      var list = items();
      if (!list.length) return;
      if (cursor > -1 && list[cursor]) list[cursor].classList.remove('is-on');
      cursor += dir;
      if (cursor < 0) cursor = list.length - 1;
      if (cursor >= list.length) cursor = 0;
      list[cursor].classList.add('is-on');
      if (list[cursor].scrollIntoView) list[cursor].scrollIntoView({ block: 'nearest' });
    }

    function run() {
      var q = norm(input.value);
      if (q.length < CFG.minChars) { showTrending(); return; }

      var mine = ++seq;
      open();

      /* 1. instant paint from the local index, or from a memoised response */
      if (ready || memo[q]) results(build(q, memo[q] || {}));
      else {
        if (!panel.classList.contains('ykps-on')) results(skeleton());
        onReady.push(function () {
          if (mine === seq) { results(build(q, memo[q] || {})); place(); }
        });
      }

      /* 2. merge Shopify's answer when it lands */
      suggest(q).then(function (res) {
        if (mine !== seq) return;
        results(build(q, res));
        place();
      });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, CFG.debounce);
    });

    input.addEventListener('focus', function () {
      if (norm(input.value).length >= CFG.minChars) run(); else showTrending();
      open();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); open(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Escape') { close(); input.blur(); }
      else if (e.key === 'Enter') {
        var list = items();
        if (cursor > -1 && list[cursor]) { e.preventDefault(); window.location.href = list[cursor].href; }
      }
    });

    document.addEventListener('click', function (e) { if (!wrap.contains(e.target)) close(); });
    window.addEventListener('resize', function () { if (wrap.classList.contains('open')) place(); });

    window.YK_PS = {
      index: function () { return IDX; },
      ready: function () { return ready; },
      terms: localTerms,
      products: localProducts,
      score: score
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  setTimeout(init, 800);
})();
